/**
 * Controller support (owner, Sep 12: "controller support… full configuration for quick buttons, driving/flying/walking";
 * docs/DESIGN_CONTROLLER.md). Pure: what a pad's buttons mean in each situation, the player's own bindings, and how a frame of
 * pad state folds into the `InputState` the game already reads. The browser side (reading `navigator.getGamepads()`) is a
 * few lines at the bottom; everything above it runs without a window, so it has tests.
 */
import type { InputState } from './input';

/** what the player is doing — the pad's buttons mean different things at the wheel and on foot, as the touch buttons do */
export type PadMode = 'walk' | 'drive' | 'fly';
export const PAD_MODES: PadMode[] = ['walk', 'drive', 'fly'];
export const MODE_NAME: Record<PadMode, string> = { walk: 'On foot', drive: 'Driving', fly: 'Flying' };

/** everything a button can do; `everywhere` actions are bound once and apply in every mode */
export type PadAction =
  | 'fire' | 'sprint' | 'jump' | 'enter' | 'reload' | 'use' | 'nextWeapon' | 'prevWeapon' | 'inventory' | 'jobs'
  | 'gas' | 'brake' | 'handbrake' | 'lights' | 'siren' | 'radio' | 'windows' | 'carMenu'
  | 'up' | 'down'
  | 'phone' | 'quick1' | 'quick2' | 'quick3' | 'quick4' | 'chat' | 'menu' | 'zoomIn' | 'zoomOut' | 'bug';
export const ACTION_NAME: Record<PadAction, string> = {
  fire: 'Fire', sprint: 'Run', jump: 'Jump', enter: 'Get in / out', reload: 'Reload', use: 'Eat, drink, shop (✚)', nextWeapon: 'Next weapon', prevWeapon: 'Previous weapon', inventory: 'Backpack', jobs: 'Jobs',
  gas: 'Gas', brake: 'Brake / reverse', handbrake: 'Handbrake', lights: 'Lights', siren: 'Siren', radio: 'Radio: next station', windows: 'Windows', carMenu: 'The Car list',
  up: 'Up', down: 'Down',
  phone: 'Phone (open / close)', quick1: 'Quick button 1', quick2: 'Quick button 2', quick3: 'Quick button 3', quick4: 'Quick button 4', chat: 'Messages', menu: 'Menu (the start card)', zoomIn: 'Zoom in', zoomOut: 'Zoom out', bug: 'Report a bug',
};
/** the actions each mode lists on the Controller page, in order; `everywhere` is a section of its own */
export const MODE_ACTIONS: Record<PadMode | 'everywhere', PadAction[]> = {
  walk: ['fire', 'sprint', 'jump', 'enter', 'reload', 'use', 'nextWeapon', 'prevWeapon', 'inventory', 'jobs', 'zoomIn', 'zoomOut'],
  drive: ['gas', 'brake', 'handbrake', 'enter', 'lights', 'siren', 'radio', 'windows', 'carMenu', 'fire', 'nextWeapon', 'prevWeapon'],
  fly: ['up', 'down', 'enter', 'fire', 'nextWeapon', 'prevWeapon'],
  everywhere: ['phone', 'quick1', 'quick2', 'quick3', 'quick4', 'chat', 'menu', 'bug'],
};

/** a button of the standard mapping (https://w3c.github.io/gamepad/#remapping): 0 A · 1 B · 2 X · 3 Y · 4 LB · 5 RB · 6 LT · 7 RT ·
 *  8 Back · 9 Start · 10 L3 · 11 R3 · 12–15 D-pad up down left right · 16 Guide */
export const BUTTON_NAME = ['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'Back', 'Start', 'L3', 'R3', 'D-pad ↑', 'D-pad ↓', 'D-pad ←', 'D-pad →', 'Guide'];
export function buttonName(i: number | undefined): string { return i === undefined ? '—' : BUTTON_NAME[i] ?? `button ${i}`; }

export interface PadBindings {
  /** button index per action, per mode; a missing action is unbound */
  walk: Partial<Record<PadAction, number>>; drive: Partial<Record<PadAction, number>>; fly: Partial<Record<PadAction, number>>; everywhere: Partial<Record<PadAction, number>>;
  /** driving: the triggers are gas and brake (default), or the stick's Y is the throttle as on the keyboard */
  driveWith: 'triggers' | 'stick';
  /** the sticks' radial dead zone */
  deadzone: number;
  /** flying: push the stick forward to climb rather than dive */
  invertFlyY: boolean;
}
export const DEFAULT_BINDINGS: PadBindings = {
  walk: { fire: 7, sprint: 6, jump: 0, enter: 1, reload: 2, use: 3, prevWeapon: 4, nextWeapon: 5, zoomOut: 10, zoomIn: 11 },
  drive: { gas: 7, brake: 6, handbrake: 0, enter: 1, lights: 2, radio: 3, siren: 4, fire: 5, carMenu: 11 },
  fly: { up: 7, down: 6, enter: 1, fire: 5 },
  everywhere: { quick1: 12, quick2: 15, quick3: 13, quick4: 14, phone: 9, chat: 8, menu: 16 },
  driveWith: 'triggers', deadzone: 0.15, invertFlyY: false,
};
export function defaultBindings(): PadBindings { return { walk: { ...DEFAULT_BINDINGS.walk }, drive: { ...DEFAULT_BINDINGS.drive }, fly: { ...DEFAULT_BINDINGS.fly }, everywhere: { ...DEFAULT_BINDINGS.everywhere }, driveWith: 'triggers', deadzone: 0.15, invertFlyY: false }; }
/** saved bindings on top of the defaults: unknown keys dropped, numbers checked — a hand-edited or an older save must not break the pad */
export function parseBindings(json: string | null | undefined): PadBindings {
  const b = defaultBindings();
  if (!json) return b;
  try {
    const o = JSON.parse(json) as Partial<PadBindings> & Record<string, unknown>;
    for (const m of ['walk', 'drive', 'fly', 'everywhere'] as const) {
      const src = o[m]; if (!src || typeof src !== 'object') continue;
      const out: Partial<Record<PadAction, number>> = {};
      for (const a of MODE_ACTIONS[m]) { const v = (src as Record<string, unknown>)[a]; if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 32) out[a] = v; }
      b[m] = out;
    }
    if (o.driveWith === 'stick' || o.driveWith === 'triggers') b.driveWith = o.driveWith;
    if (typeof o.deadzone === 'number' && o.deadzone >= 0 && o.deadzone <= 0.5) b.deadzone = o.deadzone;
    if (typeof o.invertFlyY === 'boolean') b.invertFlyY = o.invertFlyY;
  } catch { /* a broken save: the defaults */ }
  return b;
}
export const PAD_STORAGE_KEY = 'sprawl.pad';
export function loadBindings(): PadBindings { try { return parseBindings(typeof localStorage === 'undefined' ? null : localStorage.getItem(PAD_STORAGE_KEY)); } catch { return defaultBindings(); } }
export function saveBindings(b: PadBindings): void { try { localStorage.setItem(PAD_STORAGE_KEY, JSON.stringify(b)); } catch { /* no storage: the bindings last the session */ } }

/** one frame of a pad: the sticks after the dead zone, the triggers 0..1, which buttons are down and which went down this frame */
export interface PadFrame { lx: number; ly: number; rx: number; ry: number; lt: number; rt: number; down: boolean[]; edge: boolean[]; any: boolean }

/** a radial dead zone: nothing inside it, the rest rescaled so the edge of the zone is 0 and the rim is 1 */
export function deadzone(x: number, y: number, dz: number): [number, number] {
  const m = Math.hypot(x, y);
  if (m <= dz) return [0, 0];
  const k = Math.min(1, (m - dz) / (1 - dz)) / m;
  return [x * k, y * k];
}

/** the raw values of a pad, as the browser hands them (or a test): buttons as 0..1, axes as -1..1 */
export interface PadRaw { axes: readonly number[]; buttons: readonly { pressed: boolean; value: number }[] }
export class PadReader {
  private prev: boolean[] = [];
  /** fold a raw pad into a frame; call once per game frame, so the edges are one frame wide */
  read(raw: PadRaw, dz: number): PadFrame {
    const [lx, ly] = deadzone(raw.axes[0] ?? 0, raw.axes[1] ?? 0, dz), [rx, ry] = deadzone(raw.axes[2] ?? 0, raw.axes[3] ?? 0, dz);
    const down: boolean[] = [], edge: boolean[] = []; let any = false;
    for (let i = 0; i < raw.buttons.length; i++) { const b = raw.buttons[i], on = b.pressed || b.value > 0.5; down[i] = on; edge[i] = on && !this.prev[i]; if (on) any = true; }
    this.prev = down;
    // the triggers as analog where the pad reports them so (the standard mapping's 6 and 7 carry a value)
    const lt = raw.buttons[6] ? Math.max(raw.buttons[6].value, raw.buttons[6].pressed ? 1 : 0) : 0, rt = raw.buttons[7] ? Math.max(raw.buttons[7].value, raw.buttons[7].pressed ? 1 : 0) : 0;
    return { lx, ly, rx, ry, lt, rt, down, edge, any };
  }
  reset(): void { this.prev = []; }
}

export function padMode(driving: boolean, flying: boolean): PadMode { return flying ? 'fly' : driving ? 'drive' : 'walk'; }
/** the button bound to an action in a mode: the mode's own binding, else the `everywhere` one */
export function bindingOf(b: PadBindings, mode: PadMode, action: PadAction): number | undefined { return b[mode][action] ?? b.everywhere[action]; }

/** what a frame asks of the game beyond the InputState: the phone, the quick buttons, the menu, the chat (main.ts acts on these) */
export interface PadRequests { phone?: boolean; quick?: number; chat?: boolean; menu?: boolean }
/**
 * Fold a frame into the input state for a mode. The state's fields are OR-ed / added, never cleared: the keyboard and the
 * touch buttons keep their say, and the caller decides the movement precedence (`useStick`: the keys are idle).
 */
export function applyPad(f: PadFrame, b: PadBindings, mode: PadMode, s: InputState, useStick: boolean, heliStick: boolean): PadRequests {
  const held = (a: PadAction): boolean => { const i = bindingOf(b, mode, a); return i !== undefined && !!f.down[i]; };
  const tap = (a: PadAction): boolean => { const i = bindingOf(b, mode, a); return i !== undefined && !!f.edge[i]; };
  const analog = (a: PadAction): number => { const i = bindingOf(b, mode, a); if (i === undefined) return 0; return i === 6 ? f.lt : i === 7 ? f.rt : f.down[i] ? 1 : 0; };
  // movement
  if (useStick) {
    if (mode === 'drive') {
      s.moveX = f.lx;
      s.moveY = b.driveWith === 'triggers' ? Math.max(-1, Math.min(1, analog('brake') - analog('gas'))) : f.ly; // (forward is −1, as W is)
      if (b.driveWith === 'triggers' && !s.moveY) s.moveY = f.ly; // (the stick still drives when the triggers are idle: a pad with digital triggers, or an old habit)
    } else if (mode === 'fly') { s.moveX = f.lx; s.moveY = b.invertFlyY ? -f.ly : f.ly; void heliStick; }
    else { s.moveX = f.lx; s.moveY = f.ly; }
    s.driveX = Math.max(-1, Math.min(1, s.moveX)); s.driveY = Math.max(-1, Math.min(1, s.moveY));
    if (mode !== 'drive') { const m = Math.hypot(s.moveX, s.moveY); if (m > 1) { s.moveX /= m; s.moveY /= m; } } // (a wheel and a pedal are not a direction — stickAxes says why)
  }
  // aim: the right stick, when pushed
  const rm = Math.hypot(f.rx, f.ry); if (rm > 0.3) { s.aimX = f.rx / rm; s.aimY = f.ry / rm; }
  // held actions
  if (held('fire')) s.fire = true;
  if (mode === 'walk' && held('sprint')) s.sprint = true;
  if (mode === 'drive' && held('handbrake')) s.handbrake = true;
  if (mode === 'fly') { if (analog('up') > 0.3) s.up = true; if (analog('down') > 0.3) s.down = true; }
  if (held('use')) s.useHeld = true;
  // taps (edge flags, consumed by the game this frame)
  if (mode === 'walk' && tap('jump')) s.jump = true;
  if (tap('enter')) s.enter = true;
  if (tap('reload')) s.reload = true;
  if (tap('use')) s.use = true;
  if (tap('nextWeapon')) s.nextWeapon = true;
  if (tap('prevWeapon')) s.prevWeapon = true;
  if (tap('inventory')) s.inventory = true;
  if (tap('jobs')) s.jobs = true;
  if (mode === 'drive') { if (tap('lights')) s.lights = true; if (tap('siren')) s.siren = true; if (tap('radio')) s.radio = true; if (tap('windows')) s.windows = true; if (tap('carMenu')) s.carMenu = true; }
  if (tap('zoomIn')) s.zoomDelta += 0.1; if (tap('zoomOut')) s.zoomDelta -= 0.1;
  if (tap('bug')) s.bug = true;
  const req: PadRequests = {};
  if (tap('phone')) req.phone = true;
  for (let q = 0; q < 4; q++) if (tap(`quick${q + 1}` as PadAction)) req.quick = q;
  if (tap('chat')) req.chat = true;
  if (tap('menu')) req.menu = true;
  return req;
}

/** the phone open (or a list up): the D-pad walks the taps on screen, A taps, B backs out — fixed, not bindable */
export interface PadPhoneMoves { up: boolean; down: boolean; left: boolean; right: boolean; ok: boolean; back: boolean; phone: boolean }
export function phoneMoves(f: PadFrame, b: PadBindings): PadPhoneMoves {
  return { up: !!f.edge[12], down: !!f.edge[13], left: !!f.edge[14], right: !!f.edge[15], ok: !!f.edge[0], back: !!f.edge[1], phone: b.everywhere.phone !== undefined && !!f.edge[b.everywhere.phone] };
}

/** the first connected pad the browser reports, or none */
export function firstPad(): (PadRaw & { id: string; index: number }) | undefined {
  try {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) if (p && p.connected) return { id: p.id, index: p.index, axes: p.axes, buttons: p.buttons };
  } catch { /* a browser without the API */ }
  return undefined;
}
/** a short name for the settings row: the pad's own id trimmed of the vendor numbers */
export function padLabel(id: string | undefined): string { if (!id) return 'none'; const s = id.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim(); return (s.length > 26 ? `${s.slice(0, 24)}…` : s) || 'gamepad'; }
